/**
 * Saved Views bar: pinned-view chips for one-click switching, a "Views" button
 * that opens a slide-out drawer (the full list with rename / reorder / pin /
 * default / delete-confirm, a save row with a "pin ship & date range" opt-in, and
 * import/export). Views live in localStorage
 * (`state/presets.ts`); `activeViewId` is an atom so the chips + rows highlight
 * the loaded view and the drawer can offer "Update".
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { periodComparison } from '../../state/period-comparison.ts';
import { ref } from 'lit/directives/ref.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { activeTab } from '../../state/store.ts';
import {
  type SavedView,
  activeViewId,
  sortedViews,
  viewSummary,
  saveCurrentView,
  updateActiveView,
  deleteView,
  renameView,
  toggleViewPin,
  toggleViewDefault,
  reorderViews,
  loadView,
  exportViews,
  importViews,
} from '../../state/presets.ts';

@customElement('presets-bar')
export class PresetsBar extends LightElement {
  private store = new StoreController(this, [activeViewId, activeTab, periodComparison]);

  @state() private drawerOpen = false;
  @state() private nameDraft = '';
  @state() private pinContext = false;
  @state() private renamingId: string | null = null;
  @state() private pendingDeleteId: string | null = null;

  private dragId: string | null = null;

  private onKeydown = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    if (this.drawerOpen) this.closeDrawer();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKeydown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKeydown);
  }

  /* ── drawer open/close ──────────────────────────────────── */

  private openDrawer(): void {
    this.drawerOpen = true;
    this.pendingDeleteId = null;
    // Match the pin-context checkbox to the active view, if any.
    const active = activeViewId.value
      ? sortedViews().find((v) => v.id === activeViewId.value)
      : undefined;
    this.pinContext = periodComparison.value ? true : !!active?.bindContext;
    requestAnimationFrame(() =>
      this.querySelector<HTMLInputElement>('#view-name-input')?.focus(),
    );
  }

  private closeDrawer(): void {
    this.drawerOpen = false;
    this.renamingId = null;
    this.pendingDeleteId = null;
  }

  /* ── actions ────────────────────────────────────────────── */

  private submitSave(): void {
    const name = this.nameDraft.trim();
    if (!name) {
      this.querySelector<HTMLInputElement>('#view-name-input')?.focus();
      return;
    }
    saveCurrentView(name, this.pinContext);
    this.nameDraft = '';
  }

  private onLoad(id: string): void {
    this.closeDrawer();
    void loadView(id);
  }

  private commitRename(id: string, value: string): void {
    renameView(id, value);
    this.renamingId = null;
  }

  private onDelete(id: string): void {
    if (this.pendingDeleteId !== id) {
      this.pendingDeleteId = id;
      setTimeout(() => {
        if (this.pendingDeleteId === id) {
          this.pendingDeleteId = null;
          this.requestUpdate();
        }
      }, 3000);
      return;
    }
    this.pendingDeleteId = null;
    deleteView(id);
  }

  private onImportFile(e: Event): void {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) void importViews(file);
  }

  /* ── drag-reorder ───────────────────────────────────────── */

  private onDragStart(e: DragEvent, id: string): void {
    this.dragId = id;
    e.dataTransfer?.setData('text/plain', id);
    (e.currentTarget as HTMLElement).classList.add('dragging');
  }
  private onDragEnd(e: DragEvent): void {
    this.dragId = null;
    (e.currentTarget as HTMLElement).classList.remove('dragging');
    this.querySelectorAll('.view-row').forEach((el) => el.classList.remove('drop-target'));
  }
  private onDragOver(e: DragEvent, id: string): void {
    if (!this.dragId || this.dragId === id) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).classList.add('drop-target');
  }
  private onDragLeave(e: DragEvent): void {
    (e.currentTarget as HTMLElement).classList.remove('drop-target');
  }
  private onDrop(e: DragEvent, id: string): void {
    e.preventDefault();
    (e.currentTarget as HTMLElement).classList.remove('drop-target');
    if (this.dragId && this.dragId !== id) reorderViews(this.dragId, id);
    this.dragId = null;
  }

  /* ── rendering ──────────────────────────────────────────── */

  private renderChips(): TemplateResult | typeof nothing {
    const pinned = sortedViews().filter((v) => v.pinned);
    if (!pinned.length) return nothing;
    return html`<span class="view-chips">
      ${pinned.map(
        (v) => html`<button
          class="view-chip ${v.id === activeViewId.value ? 'active' : ''}"
          title=${`${v.name} - ${viewSummary(v)}`}
          @click=${() => this.onLoad(v.id)}
        >
          <span class="vc-star">★</span><span class="vc-name">${v.name}</span>
        </button>`,
      )}
    </span>`;
  }

  private renderRow(v: SavedView): TemplateResult {
    const active = v.id === activeViewId.value;
    const confirming = v.id === this.pendingDeleteId;
    const renaming = v.id === this.renamingId;
    return html`<div
      class="view-row ${active ? 'active' : ''}"
      draggable=${!renaming}
      title="Load this view"
      @dragstart=${(e: DragEvent) => this.onDragStart(e, v.id)}
      @dragend=${this.onDragEnd}
      @dragover=${(e: DragEvent) => this.onDragOver(e, v.id)}
      @dragleave=${this.onDragLeave}
      @drop=${(e: DragEvent) => this.onDrop(e, v.id)}
    >
      <span class="vr-grip" title="Drag to reorder">⠿</span>
      <div class="vr-main" @click=${() => !renaming && this.onLoad(v.id)}>
        ${renaming
          ? html`<input
              class="vr-rename-input"
              type="text"
              maxlength="60"
              .value=${v.name}
              @click=${(e: Event) => e.stopPropagation()}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') this.commitRename(v.id, (e.target as HTMLInputElement).value);
                else if (e.key === 'Escape') this.renamingId = null;
              }}
              @blur=${(e: Event) => this.commitRename(v.id, (e.target as HTMLInputElement).value)}
              ${ref((el) => el && (el as HTMLInputElement).focus())}
            />`
          : html`<div class="vr-name">
                <span class="vr-name-text">${v.name}</span>
                ${v.isDefault ? html`<span class="vr-badge default">default</span>` : nothing}
                ${v.bindContext
                  ? html`<span class="vr-badge" title="Stores ship & date range">pinned ctx</span>`
                  : nothing}
              </div>
              <div class="vr-meta">${viewSummary(v)}</div>`}
      </div>
      <div class="vr-actions">
        <button
          class="vr-act ${v.pinned ? 'on' : ''}"
          title=${v.pinned ? 'Unpin from header' : 'Pin to header chips'}
          @click=${(e: Event) => {
            e.stopPropagation();
            toggleViewPin(v.id);
          }}
        >
          ★
        </button>
        <button
          class="vr-act ${v.isDefault ? 'default-on' : ''}"
          title=${v.isDefault ? 'Unset as default' : 'Set as default (auto-loads on open)'}
          @click=${(e: Event) => {
            e.stopPropagation();
            toggleViewDefault(v.id);
          }}
        >
          ⚑
        </button>
        <button
          class="vr-act"
          title="Rename"
          @click=${(e: Event) => {
            e.stopPropagation();
            this.renamingId = v.id;
          }}
        >
          ✎
        </button>
        <button
          class="vr-act del ${confirming ? 'confirm' : ''}"
          title=${confirming ? 'Click again to confirm' : 'Delete'}
          @click=${(e: Event) => {
            e.stopPropagation();
            this.onDelete(v.id);
          }}
        >
          ${confirming ? 'Sure?' : '✕'}
        </button>
      </div>
    </div>`;
  }

  private renderDrawer(): TemplateResult {
    const views = sortedViews();
    const active = activeViewId.value ? views.find((v) => v.id === activeViewId.value) : undefined;
    return html`
      <div class="views-overlay ${this.drawerOpen ? 'open' : ''}" @click=${() => this.closeDrawer()}></div>
      <div class="views-drawer ${this.drawerOpen ? 'open' : ''}" role="dialog" aria-label="Saved views">
        <div class="vd-head">
          <span class="vd-title">★ Saved views</span>
          <button class="vd-close" title="Close" @click=${() => this.closeDrawer()}>×</button>
        </div>
        <div class="vd-list">
          ${views.length
            ? views.map((v) => this.renderRow(v))
            : html`<div class="vd-empty">
                No saved views yet.<br />Configure a chart, then save it below.
              </div>`}
        </div>
        <div class="vd-foot">
          ${active
            ? html`<button
                class="vd-io-btn vd-update"
                title="Save the current chart config back into the active view"
                @click=${() => updateActiveView()}
              >
                ↻ Update “${active.name}” with current chart
              </button>`
            : nothing}
          <div class="vd-save-row">
            <input
              type="text"
              id="view-name-input"
              placeholder="Name this view…"
              maxlength="60"
              .value=${this.nameDraft}
              @input=${(e: Event) => (this.nameDraft = (e.target as HTMLInputElement).value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') this.submitSave();
              }}
            />
            <button class="vd-save-btn" @click=${() => this.submitSave()}>Save view</button>
          </div>
          <label
            class="vd-pin-row"
            title="Also store the current ship and date range, so loading this view jumps straight to them. Off = the view applies to whatever ship/range you have open."
          >
            <input
              type="checkbox"
              .checked=${this.pinContext}
              @change=${(e: Event) => (this.pinContext = (e.target as HTMLInputElement).checked)}
            />
            ${periodComparison.value ? 'Pin ship & periods to this view' : 'Pin ship & date range to this view'}
          </label>
          ${periodComparison.value && !this.pinContext
            ? html`<div class="vd-context-note">Chart settings only: fixed comparison periods will be omitted.</div>`
            : periodComparison.value
              ? html`<div class="vd-context-note">Period comparison requires fixed ship and dates.</div>`
              : nothing}
          <div class="vd-io-row">
            <button class="vd-io-btn" title="Download all saved views as JSON" @click=${() => exportViews()}>
              ↓ Export
            </button>
            <button
              class="vd-io-btn"
              title="Import views from a JSON file"
              @click=${() => this.querySelector<HTMLInputElement>('#views-import-file')?.click()}
            >
              ↑ Import
            </button>
            <input
              type="file"
              id="views-import-file"
              accept="application/json,.json"
              style="display:none"
              @change=${(e: Event) => this.onImportFile(e)}
            />
          </div>
        </div>
      </div>
    `;
  }

  override render(): TemplateResult {
    // Saved views only fully apply to the Plot tab, so the chips + Views drawer
    // are hidden everywhere else.
    const showViews = activeTab.value === 'plot';
    return html`
      ${showViews ? this.renderChips() : nothing}
      ${showViews
        ? html`<button
            class="chart-icon-btn is-parked"
            title="Saved views"
            aria-haspopup="dialog"
            aria-expanded=${this.drawerOpen}
            @click=${() => this.openDrawer()}
          >
            ★ Views
          </button>`
        : nothing}
      ${showViews ? this.renderDrawer() : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'presets-bar': PresetsBar;
  }
}
